import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import PhoneNumber from 'awesome-phonenumber';
import { ICountry, IState, ErrorHandlerService, CommonService, RegisterLibraryService, AuthService, PhoneHelperService } from 'epay-library';
import { IEmpProfileAccountLogin } from 'epay-library/lib/features/employer/models/user/profile/employerProfileAccountLogin';
import { IEmpProfileAccountResults } from 'epay-library/lib/features/employer/models/user/profile/employerProfileAccountResults';
import { debounceTime } from 'rxjs';

@Component({
  selector: 'epay-employer-profile-detail',
  templateUrl: './epay-employer-profile-detail.component.html'
})
export class EpayEmployerProfileDetailComponent implements OnInit {
  @Input() baseurl = '';
  @Input() useEmailForLogin = false;
  @Output() profileConfirmationEmitter = new EventEmitter<{
    success: boolean;
    message: string;
    confirmationNumber: string;
    showEmailMismatch?: boolean;
  }>();

  isAccountManager = false;
  isLoggedIn = false;
  userNumber = '';
  userName = '';
  countries: ICountry[] = [];
  selectedCountry = '';
  states: IState[] = [];
  selectedState = '';
  errorMessage = '';
  emailMessage = '';
  profileAccountResults: IEmpProfileAccountResults =
    {} as IEmpProfileAccountResults;
  profileAccounts: IEmpProfileAccountLogin[];
  accountFrmGrp: FormGroup = new FormGroup({});

  isValidEmail = false;
  isCheckingEmail = false;
  emailErrorMessage = '';
  emailSuccessMessage = '';
  originalEmail = '';
  email = '';

  phonePattern = /^((\\+91-?)|0)?[0-9]{10}$/;
  loginPattern =
    /(?=^[A-Za-z\d]{8,12}$)(?=(.*[A-Za-z]){6,8})(?=(.*\d){2,4})^.*/;
  zipPattern = /^[0-9]{5}$/;
  emailPattern = /\S+@\S+\.\S+/;
  phoneExample = '';
  phoneMask = '';
  newUsername = '';

  validationEmailMessages: { [key: string]: string } = {
    required: 'Required.',
    pattern: 'Please enter a valid email address.',
    isDuplicate: 'Registration already exist for this email.',
  };

  constructor(
    private formbuilder: FormBuilder,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    public dialog: MatDialog,
    public snackbar: MatSnackBar,
    private commonService: CommonService,
    private registerService: RegisterLibraryService,
    private authservice: AuthService,
    private phoneHelperService: PhoneHelperService
  ) {
    this.profileAccounts = [];
    this.selectedCountry = 'US';
  }

  ngOnInit() {
    this.authservice.isLoggedIn.subscribe(
      (status) => (this.isLoggedIn = status)
    );
    this.authservice.currentUserNumber.subscribe(
      (num) => (this.userNumber = num)
    );
    this.authservice.currentLoginId.subscribe((name) => (this.userName = name));

    this.loadFromGroups();
    this.getCountries();
    this.getStates();
    this.loadEmployerAccounts();

    const phoneCtrl = this.accountFrmGrp.get('phoneGp.phoneNumber');
    const countryCtrl = this.accountFrmGrp.get('country');
    const emailCtrl = this.accountFrmGrp.get('email');

    emailCtrl!.valueChanges
      .pipe(debounceTime(500))
      .subscribe((email) => this.setEmailMessage(emailCtrl!, email));

    phoneCtrl!.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      if (value) {
        const pn = new PhoneNumber(
          value,
          this.accountFrmGrp.get('phoneGp.regionCode')!.value
        );
        this.accountFrmGrp.get('phoneGp')!.patchValue({
          isValid: pn.isPossible(),
        });
      }
    });

    countryCtrl!.valueChanges.subscribe((value) => {
      if (value) {
        this.setMaskAndExample(value);
        this.accountFrmGrp.patchValue({
          state: '',
          zip5: '',
        });
        this.accountFrmGrp.get('phoneGp')!.patchValue({
          regionCode: value,
          countryCode:
            this.phoneHelperService.getCountryCodeForRegionCode(value),
          phoneNumber: '',
          isValid: false,
        });
        if (value === 'US') {
          this.accountFrmGrp
            .get('zip5')!
            .setValidators([
              Validators.required,
              Validators.pattern(this.zipPattern),
            ]);
          this.accountFrmGrp.get('zip5')!.updateValueAndValidity();
        } else {
          this.accountFrmGrp
            .get('zip5')!
            .setValidators([Validators.required, Validators.maxLength(6)]);
          this.accountFrmGrp.get('zip5')!.updateValueAndValidity();
        }
        if (value === 'US' || value === 'MX' || value === 'CA') {
          this.selectedCountry = value;
          this.accountFrmGrp.get('state')!.enable();
          this.accountFrmGrp.get('state')!.setErrors({ required: true });
          this.accountFrmGrp.updateValueAndValidity();
          this.getStates();
        } else {
          this.accountFrmGrp.get('state')!.disable();
          this.accountFrmGrp.get('state')!.setErrors({ required: false });
          this.accountFrmGrp.updateValueAndValidity();
        }
      }
    });
  }

  updateAccount() {
    if (this.accountFrmGrp.valid) {
      if (this.accountFrmGrp.dirty) {
        const account = {
          firstName: this.accountFrmGrp.get('firstName')!.value.trim(),
          middleName:
            this.accountFrmGrp.get('middleName')!.value === null
              ? ''
              : this.accountFrmGrp.get('middleName')!.value.trim(),
          lastName: this.accountFrmGrp.get('lastName')!.value.trim(),
          address1: this.accountFrmGrp.get('address1')!.value.trim(),
          address2:
            this.accountFrmGrp.get('address2')!.value === null
              ? ''
              : this.accountFrmGrp.get('address2')!.value.trim(),
          city: this.accountFrmGrp.get('city')!.value.trim(),
          state: this.accountFrmGrp.get('state')!.value.trim(),
          zip5: this.accountFrmGrp.get('zip5')!.value.trim(),
          zip4: '',
          countryCode:
            this.accountFrmGrp.get('country')!.value === null
              ? 'US'
              : this.accountFrmGrp.get('country')!.value.trim(),
          phone: this.accountFrmGrp.get('phoneGp.phoneNumber')!.value.trim(),
          phoneExt:
            this.accountFrmGrp.get('phoneExt')!.value === null
              ? ''
              : this.accountFrmGrp.get('phoneExt')!.value.trim(),
          email: this.accountFrmGrp.get('email')!.value.trim(),
        };

        this.registerService
          .updateUserAccount(this.baseurl, account, +this.userNumber)
          .subscribe(
            (response: any) => {
              this.authservice
                .getAccountDetails(this.baseurl, this.userName)
                .subscribe({
                  next: (accountDetails: any) => {},
                  error: (error: any) => {
                    this.logError(error);
                    this.handleError(error.status);
                  },
                });
              this.profileConfirmationEmitter.emit({
                success: true,
                message:
                  'Account for ' +
                  account.firstName +
                  ' ' +
                  account.lastName +
                  ' has been updated.',
                confirmationNumber: response.confirmationNumber,
                showEmailMismatch:
                  this.originalEmail !==
                  this.accountFrmGrp.get('email')!.value.trim()
                    ? true
                    : false,
              });
              this.onSaveComplete();
            },
            (error: any) => {
              this.logError(error);
              this.handleError(error.status);
            }
          );
      }
    }
  }

  loadEmployerAccounts() {
    this.profileAccounts = [];
    this.registerService.getEmployerProfileAccounts(this.baseurl).subscribe({
      next: (accounts: any) => {
        this.profileAccountResults = accounts;
        this.profileAccountResults.loginDetails.forEach((account) => {
          if (+this.userNumber === +account.userNumberExternal) {
            this.accountFrmGrp.patchValue(
              {
                loginId: account.loginId,
                firstName: account.contactInfo.firstName,
                middleName: account.contactInfo.middleName,
                lastName: account.contactInfo.lastName,
                address1: account.contactInfo.address1,
                address2: account.contactInfo.address2,
                country: account.contactInfo.countryId,
                city: account.contactInfo.city,
                zip5: account.contactInfo.zip5.trim(),
                phoneExt: account.contactInfo.phoneExt,
                state: account.contactInfo.state,
              },
              { emitEvent: true }
            );
            this.accountFrmGrp.patchValue(
              {
                email: account.contactInfo.email,
              },
              { emitEvent: false }
            );

            this.originalEmail = account.contactInfo.email;
            if (account.contactInfo.state.trim() !== '') {
              this.accountFrmGrp
                .get('zip5')!
                .setValidators([
                  Validators.required,
                  Validators.pattern(this.zipPattern),
                ]);
              this.accountFrmGrp.updateValueAndValidity();
              this.accountFrmGrp
                .get('state')!
                .setValidators([Validators.required]);
              this.accountFrmGrp.updateValueAndValidity();
            } else {
              this.accountFrmGrp
                .get('zip5')!
                .setValidators([Validators.required, Validators.maxLength(6)]);
              this.accountFrmGrp.get('zip5')!.updateValueAndValidity();
              this.accountFrmGrp.get('state')!.clearValidators();
              this.accountFrmGrp.get('state')!.disable();
              this.accountFrmGrp.updateValueAndValidity();
            }
            this.accountFrmGrp.get('phoneGp')!.patchValue(
              {
                regionCode: account.contactInfo.countryId,
                phoneNumber: account.contactInfo.phone
                  .substring(account.contactInfo.phone.trim().lastIndexOf(' '))
                  .trim(),
                countryCode:
                  this.phoneHelperService.getCountryCodeForRegionCode(
                    account.contactInfo.countryId
                  ),
                isValid: true,
              },
              { emitEvent: false }
            );
            this.setMaskAndExample(account.contactInfo.countryId);
            return;
          }
        });
      },
      error: (error: any) => {
        this.logError(error);
        this.handleError(error.status);
      },
    });
  }

  loadFromGroups() {
    this.accountFrmGrp = this.formbuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(40)]],
      middleName: ['', [Validators.maxLength(40)]],
      lastName: ['', [Validators.required, Validators.maxLength(40)]],
      address1: ['', [Validators.required, Validators.maxLength(40)]],
      address2: ['', [Validators.maxLength(40)]],
      city: ['', [Validators.required, Validators.maxLength(40)]],
      state: ['', Validators.required],
      zip5: ['', [Validators.required]],
      country: ['US', Validators.required],
      email: ['', [Validators.pattern(this.emailPattern), Validators.required]],
      phoneGp: this.formbuilder.group({
        regionCode: 'US',
        phoneNumber: ['', [Validators.required]],
        countryCode: '1',
        isValid: [false, Validators.requiredTrue],
      }),
      phoneExt: '',
    });

    // set default country to US
    this.accountFrmGrp.patchValue({
      country: 'US',
    });
  }

  onSaveComplete() {
    this.accountFrmGrp.reset();
  }

  getDemographicErrorMessage(name: any): string {
    let message = '';
    const fieldControl = this.accountFrmGrp.get(name);
    if (fieldControl!.touched || fieldControl!.dirty) {
      if (fieldControl!.hasError('required')) {
        message = 'Required.';
      } 
      else if (fieldControl!.hasError('maxlength')) {
        message = 'Field must be 40 characters or less.';
      }
      else {
        message = 'Invalid Format.';
      }
    }
    return message;
  }

  getCountries() {
    this.commonService.getCountries(this.baseurl).subscribe(
      (countries) => {
        this.countries = countries;
        this.countries = this.countries.sort((a, b) =>
          a.countryName < b.countryName ? 1 : -1
        );
        this.countries.push({
          country: 'MX',
          countryName: 'Mexico',
          alpha3IsoCode: 'MEX',
          numeric3IsoCode: '484',
        });
        this.countries.push({
          country: 'CA',
          countryName: 'Canada',
          alpha3IsoCode: 'CAN',
          numeric3IsoCode: '124',
        });
        this.countries.push({
          country: 'US',
          countryName: 'United States',
          alpha3IsoCode: 'USA',
          numeric3IsoCode: '840',
        });
        this.countries.reverse();
      },
      (error) => {
        this.logError(error);
        this.handleError(error.status);
      }
    );
  }

  getStates() {
    this.selectedCountry = this.accountFrmGrp.get('country')!.value;
    this.commonService.getStates(this.baseurl, this.selectedCountry).subscribe(
      (states) => {
        this.states = states;
      },
      (error) => {
        this.logError(error);
        this.handleError(error.status);
      }
    );
  }

  setEmailMessage(emailControl: AbstractControl, email: any): void {
    if (this.originalEmail !== email) {
      this.resetEmailCheck();
      if (emailControl.touched || emailControl.dirty) {
        if (!emailControl.errors) {
          this.isCheckingEmail = true;
          this.registerService
            .searchDuplicateEmail(this.baseurl, email)
            .subscribe({
              next: (result: any) => {
                this.isValidEmail = !result.isDuplicate;

                this.isCheckingEmail = false;
              },
              error: (error: any) => {
                this.resetEmailCheck();
                this.errorMessage = error as any;
                this.logError(error);
                this.handleError(error.status);
              },
              complete: () => {
                if (this.isValidEmail) {
                  this.emailSuccessMessage = 'Email is available!';
                } else {
                  emailControl.setErrors({ isDuplicate: true });
                  this.emailErrorMessage = Object.keys(emailControl.errors!)
                    .map((key) => this.validationEmailMessages[key])
                    .join(' ');
                }
              },
            });
        } else {
          this.emailErrorMessage = Object.keys(emailControl.errors)
            .map((key) => this.validationEmailMessages[key])
            .join(' ');
        }
      }
    } else {
      this.isValidEmail = true;
      this.emailSuccessMessage = 'This is your current email address.';
    }
  }

  resetEmailCheck() {
    this.isValidEmail = false;
    this.isCheckingEmail = false;
    this.emailSuccessMessage = '';
    this.emailErrorMessage = '';
  }

  setMaskAndExample(regionCode: string) {
    this.phoneExample = this.phoneHelperService.getExample(regionCode);
    this.phoneMask = this.phoneHelperService.getMask(regionCode);
  }

  goBackInBrowser() {
    this.accountFrmGrp.reset();
    this.commonService.goBackInBrowser();
  }

  openSnackBar(message: string, time: number, style: string) {
    const config = new MatSnackBarConfig();
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = time;
    config.panelClass = style;
    this.snackbar.open(message, '', config);
  }

  logError(error: Error) {
    this.errorHandlerService.logError(error);
  }

  handleError(error: number) {
    console.log(this.errorMessage);
    console.log('error', error);
    this.errorHandlerService.handleErrorMessage(error, this.baseurl);
  }
}

